import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Check, ChevronDown, ChevronUp, Share2, Copy, Users, Heart, MessageCircle, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate or retrieve device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem("dismissal_pulse_device_id");
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem("dismissal_pulse_device_id", deviceId);
  }
  return deviceId;
};

const STATUS_OPTIONS = [
  { key: "dismissed", label: "Dismissed", emoji: "🏫" },
  { key: "bus", label: "On school bus", emoji: "🚌" },
  { key: "home", label: "Reached home", emoji: "🏠" },
  { key: "delayed", label: "Delayed", emoji: "⏰" },
];

export default function PulsePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pulseData, setPulseData] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showStatusButtons, setShowStatusButtons] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const fetchPulse = useCallback(async () => {
    try {
      const deviceId = getDeviceId();
      const response = await axios.get(`${API}/pulse/${token}`, {
        params: { device_id: deviceId }
      });
      setPulseData(response.data);
      setHasSubmitted(response.data.has_submitted);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("invalid");
      } else {
        setError("error");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPulse();
    
    // Auto-refresh pulse every 30 seconds for real-time updates
    const interval = setInterval(() => {
      fetchPulse();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchPulse]);

  const handleStatusSubmit = async (status) => {
    if (submitting || hasSubmitted) return;
    
    setSubmitting(true);
    try {
      const deviceId = getDeviceId();
      await axios.post(`${API}/pulse/${token}/status`, {
        status,
        device_id: deviceId
      });
      setHasSubmitted(true);
      setShowStatusButtons(false);
      // Refresh pulse data after submission
      await fetchPulse();
    } catch (err) {
      console.error("Submission error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="pulse-container" data-testid="loading-container">
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  // Error state - invalid link
  if (error) {
    return (
      <div className="pulse-container" data-testid="error-container">
        <div className="pulse-card">
          <div className="error-container">
            <p className="error-text" data-testid="error-message">
              This link is not valid.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Outside dismissal window
  if (!pulseData.within_window) {
    return (
      <div className="pulse-container" data-testid="outside-window-container">
        <div className="pulse-card">
          <div className="pulse-header">
            <h1 className="pulse-title" data-testid="class-name">{pulseData.class_name}</h1>
            <p className="pulse-subtitle">Dismissal Pulse</p>
          </div>
          <div className="neutral-message">
            <p className="neutral-text" data-testid="window-ended-message">
              {pulseData.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Active window - main view (pulse-first design)
  return (
    <div className="pulse-container" data-testid="pulse-main-container">
      <div className="pulse-card">
        <div className="pulse-header">
          <h1 className="pulse-title" data-testid="class-name">{pulseData.class_name}</h1>
          <p className="pulse-subtitle">Dismissal Pulse</p>
        </div>

        {/* Pulse Summary - Always shown first */}
        {pulseData.pulse && Object.keys(pulseData.pulse).length > 0 ? (
          <div className="pulse-summary animate-fade-in-up" data-testid="pulse-summary">
            <h3 className="pulse-summary-title">Current Pulse</h3>
            <div className="pulse-items">
              {pulseData.pulse.home && (
                <div className="pulse-item" data-testid="pulse-home">
                  <span className="pulse-dot home" />
                  <span>{pulseData.pulse.home}</span>
                </div>
              )}
              {pulseData.pulse.bus && (
                <div className="pulse-item" data-testid="pulse-bus">
                  <span className="pulse-dot bus" />
                  <span>{pulseData.pulse.bus}</span>
                </div>
              )}
              {pulseData.pulse.dismissed && (
                <div className="pulse-item" data-testid="pulse-dismissed">
                  <span className="pulse-dot dismissed" />
                  <span>{pulseData.pulse.dismissed}</span>
                </div>
              )}
              {pulseData.pulse.delayed && (
                <div className="pulse-item" data-testid="pulse-delayed">
                  <span className="pulse-dot delayed" />
                  <span>{pulseData.pulse.delayed}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="pulse-summary animate-fade-in-up" data-testid="no-updates-container">
            <div className="neutral-message" style={{ padding: "1rem 0" }}>
              <p className="neutral-text" data-testid="no-updates-message">
                {pulseData.message}
              </p>
            </div>
          </div>
        )}

        <p className="disclaimer" data-testid="disclaimer">
          This reflects updates from some parents.
        </p>

        {/* Submission Section */}
        {hasSubmitted ? (
          <div className="submission-confirmation animate-fade-in-up" data-testid="submission-confirmation">
            <div className="confirmation-inline">
              <Check size={18} strokeWidth={2.5} color="#15803D" />
              <span data-testid="confirmation-text">Thanks. Your update helps everyone stay calm.</span>
            </div>
          </div>
        ) : (
          <div className="submission-section" data-testid="submission-section">
            <button
              className="toggle-submit-btn"
              onClick={() => setShowStatusButtons(!showStatusButtons)}
              data-testid="toggle-submit-btn"
            >
              <span>Share your update</span>
              {showStatusButtons ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            {showStatusButtons && (
              <div className="status-buttons animate-fade-in-up" data-testid="status-buttons">
                {STATUS_OPTIONS.map((option, index) => (
                  <button
                    key={option.key}
                    className={`status-btn ${option.key}`}
                    style={{ animationDelay: `${index * 50}ms` }}
                    onClick={() => handleStatusSubmit(option.key)}
                    disabled={submitting}
                    data-testid={`status-btn-${option.key}`}
                  >
                    <span className="status-icon">{option.emoji}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Share Section */}
        <ShareSection classToken={token} />
      </div>

      {/* Floating Feedback Button */}
      <button
        onClick={() => setShowFeedbackModal(true)}
        style={{
          position: "fixed",
          bottom: "1.5rem",
          right: "1.5rem",
          background: "#3B82F6",
          color: "white",
          border: "none",
          borderRadius: "9999px",
          padding: "1rem",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
          cursor: "pointer",
          zIndex: 100,
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          fontFamily: "'Work Sans', sans-serif",
          fontSize: "0.875rem",
          fontWeight: "500"
        }}
        data-testid="feedback-btn"
      >
        <MessageCircle size={20} />
        <span>Feedback</span>
      </button>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          classToken={token}
        />
      )}
    </div>
  );
}

// Share Section Component
function ShareSection({ classToken }) {
  const [showShare, setShowShare] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState(false);
  const [copiedRecommend, setCopiedRecommend] = useState(false);

  const inviteLink = `${window.location.origin}/pulse/${classToken}`;
  const recommendLink = window.location.origin;

  const handleCopyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopiedInvite(true);
      setTimeout(() => setCopiedInvite(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleCopyRecommend = async () => {
    try {
      await navigator.clipboard.writeText(recommendLink);
      setCopiedRecommend(true);
      setTimeout(() => setCopiedRecommend(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleWhatsAppInvite = () => {
    const text = `Join our class dismissal pulse: ${inviteLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const handleWhatsAppRecommend = () => {
    const text = `I've been using Dismissal Pulse for our class - it helps parents stay calm after school pickup. You can start one for your class here: ${recommendLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="share-section" data-testid="share-section">
      <button
        className="toggle-share-btn"
        onClick={() => setShowShare(!showShare)}
        data-testid="toggle-share-btn"
      >
        <Share2 size={18} />
        <span>Share</span>
        {showShare ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {showShare && (
        <div className="share-options animate-fade-in-up" data-testid="share-options">
          {/* Invite to this class */}
          <div className="share-option" data-testid="invite-option">
            <div className="share-option-header">
              <Users size={16} />
              <span>Invite to this class</span>
            </div>
            <p className="share-option-desc">For parents in the same class</p>
            <div className="share-option-actions">
              <button onClick={handleCopyInvite} data-testid="copy-invite-btn" className="share-action-btn">
                {copiedInvite ? <Check size={16} /> : <Copy size={16} />}
                <span>{copiedInvite ? "Copied" : "Copy link"}</span>
              </button>
              <button onClick={handleWhatsAppInvite} data-testid="whatsapp-invite-btn" className="share-action-btn whatsapp">
                WhatsApp
              </button>
            </div>
          </div>

          {/* Recommend to a friend */}
          <div className="share-option" data-testid="recommend-option">
            <div className="share-option-header">
              <Heart size={16} />
              <span>Recommend to a friend</span>
            </div>
            <p className="share-option-desc">For friends in other classes</p>
            <div className="share-option-actions">
              <button onClick={handleCopyRecommend} data-testid="copy-recommend-btn" className="share-action-btn">
                {copiedRecommend ? <Check size={16} /> : <Copy size={16} />}
                <span>{copiedRecommend ? "Copied" : "Copy link"}</span>
              </button>
              <button onClick={handleWhatsAppRecommend} data-testid="whatsapp-recommend-btn" className="share-action-btn whatsapp">
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Feedback Modal Component
function FeedbackModal({ isOpen, onClose, classToken }) {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || loading) return;

    setLoading(true);
    try {
      const deviceId = getDeviceId();
      await axios.post(`${API}/feedback`, {
        message: message.trim(),
        class_token: classToken,
        device_id: deviceId
      });
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setMessage("");
      }, 2000);
    } catch (err) {
      console.error("Error submitting feedback:", err);
      alert("Failed to submit feedback. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        zIndex: 1000
      }}
      onClick={onClose}
      data-testid="feedback-modal-overlay"
    >
      <div
        style={{
          background: "white",
          borderRadius: "1rem",
          maxWidth: "500px",
          width: "100%",
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
          fontFamily: "'Work Sans', sans-serif"
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="feedback-modal"
      >
        <div
          style={{
            padding: "1.5rem",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <h2 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#111827" }}>
            Share Your Feedback
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: "#6B7280",
              padding: "0.25rem"
            }}
            data-testid="close-feedback-btn"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "1.5rem" }}>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "2rem 0" }}>
              <div style={{ marginBottom: "1rem" }}>
                <Check size={48} strokeWidth={2.5} color="#15803D" style={{ margin: "0 auto" }} />
              </div>
              <p style={{ color: "#15803D", fontWeight: "500", fontSize: "1.125rem" }}>
                Thank you for your feedback!
              </p>
            </div>
          ) : (
            <>
              <p style={{ color: "#6B7280", fontSize: "0.875rem", marginBottom: "1rem" }}>
                Help us improve Dismissal Pulse. Report issues, suggest features, or share your thoughts.
              </p>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your feedback here..."
                rows={6}
                maxLength={5000}
                required
                data-testid="feedback-textarea"
                style={{
                  width: "100%",
                  padding: "0.875rem",
                  borderRadius: "0.75rem",
                  border: "1px solid #E5E7EB",
                  fontSize: "1rem",
                  fontFamily: "'Work Sans', sans-serif",
                  outline: "none",
                  resize: "vertical",
                  boxSizing: "border-box"
                }}
              />
              <p style={{ fontSize: "0.75rem", color: "#9CA3AF", marginTop: "0.5rem" }}>
                {message.length}/5000 characters
              </p>
              <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem" }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: "0.875rem",
                    border: "1px solid #E5E7EB",
                    borderRadius: "0.75rem",
                    background: "white",
                    color: "#374151",
                    fontSize: "1rem",
                    fontWeight: "500",
                    cursor: "pointer",
                    fontFamily: "'Work Sans', sans-serif"
                  }}
                  data-testid="cancel-feedback-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!message.trim() || loading}
                  style={{
                    flex: 1,
                    padding: "0.875rem",
                    border: "none",
                    borderRadius: "0.75rem",
                    background: message.trim() && !loading ? "#3B82F6" : "#E5E7EB",
                    color: message.trim() && !loading ? "white" : "#9CA3AF",
                    fontSize: "1rem",
                    fontWeight: "500",
                    cursor: message.trim() && !loading ? "pointer" : "not-allowed",
                    fontFamily: "'Work Sans', sans-serif"
                  }}
                  data-testid="submit-feedback-btn"
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
